import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { ClipboardList, Download } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const SummaryCard = ({ materialTotals, grandTotal }) => {
  const exportToCSV = () => {
    let csv = 'Material,Category,Quantity (sq ft),Total Weight (lbs)\n';
    
    materialTotals.forEach(material => {
      csv += `${material.name},${material.category},${material.totalSquareFeet},${material.totalWeight.toFixed(2)}\n`;
    });
    
    csv += `\nGrand Total,,${grandTotal.totalSquareFeet},${grandTotal.totalWeight.toFixed(2)}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `material-calculation-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Report exported successfully!');
  };

  return (
    <div className="space-y-4">
      <Card className="border border-border shadow-card bg-card sticky top-4">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            Summary by Material
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {materialTotals.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No materials added yet
            </p>
          ) : (
            <>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {materialTotals.map((material, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-foreground text-sm">{material.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {material.count} {material.count === 1 ? 'entry' : 'entries'}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-muted rounded p-2">
                        <p className="text-muted-foreground mb-1">Area</p>
                        <p className="font-semibold text-foreground">
                          {material.totalSquareFeet.toLocaleString()} sq ft
                        </p>
                      </div>
                      <div className="bg-muted rounded p-2">
                        <p className="text-muted-foreground mb-1">Weight</p>
                        <p className="font-semibold text-primary">
                          {material.totalWeight.toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs
                        </p>
                      </div>
                    </div>
                    {index < materialTotals.length - 1 && <Separator className="mt-3" />}
                  </div>
                ))}
              </div>
              
              <Separator className="my-4" />
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-foreground text-sm mb-3">Grand Total</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Area:</span>
                    <span className="text-base font-bold text-foreground">
                      {grandTotal.totalSquareFeet.toLocaleString()} sq ft
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Weight:</span>
                    <span className="text-lg font-bold text-primary">
                      {(grandTotal.totalWeight / 2000).toLocaleString(undefined, { maximumFractionDigits: 2 })} tons
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-primary/20">
                    <span className="text-sm text-muted-foreground">Total Pounds:</span>
                    <span className="text-base font-bold text-primary">
                      {grandTotal.totalWeight.toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs
                    </span>
                  </div>
                </div>
              </div>
              
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="w-full mt-4 border-primary/50 text-primary hover:bg-primary/10"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SummaryCard;